"use strict";
// Copyright 2019-2020 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var DefaultBrowserBehavior_1 = require("../browserbehavior/DefaultBrowserBehavior");
var Maybe_1 = require("../maybe/Maybe");
var MediaRecordingEvent_1 = require("./MediaRecordingEvent");
var WebMMediaRecording = /** @class */ (function () {
    function WebMMediaRecording(mediaStream, options, browser) {
        if (options === void 0) { options = {}; }
        if (browser === void 0) { browser = WebMMediaRecording.browser; }
        this.mediaStream = mediaStream;
        this.browser = browser;
        this.delegate = null;
        this.listeners = new Map();
        this.options = __assign(__assign({}, options), WebMMediaRecording.options);
    }
    WebMMediaRecording.prototype.key = function () {
        var _this = this;
        if (this.delegate && this.delegate.state === 'paused') {
            return;
        }
        var delegate = this.delegate;
        var mediaStream = delegate === null ? this.mediaStream : this.mediaStream.clone();
        this.delegate = new MediaRecorder(mediaStream, this.options);
        this.delegate.addEventListener('dataavailable', function (event) {
            _this.dispatchEvent(event);
        });
        /**
         * Chrome 'ended' callback:
         * This is a Chrome-specific callback that we receive when the user clicks the "Stop Sharing" button
         * in the Chrome screen sharing bar.
         */
        this.delegate.stream.getTracks().forEach(function (track) {
            track.addEventListener('ended', function () {
                var event = new CustomEvent(MediaRecordingEvent_1.default.EndedEvent, { detail: track });
                _this.dispatchEvent(event);
            });
        });
        if (delegate !== null) {
            if (this.browser.hasChromiumWebRTC()) {
                delegate.stream.getTracks().forEach(function (stream) { return stream.stop(); });
            }
            delegate.stop();
        }
        this.delegate.start(this.timeSliceMs);
    };
    WebMMediaRecording.prototype.start = function (timeSliceMs) {
        this.timeSliceMs = timeSliceMs;
        this.key();
    };
    WebMMediaRecording.prototype.stop = function () {
        var _this = this;
        return new Promise(function (resolve, reject) {
            if (_this.delegate === null) {
                reject(new Error('not started'));
            }
            // this event should fire after any data is de-queued
            _this.delegate.addEventListener('stop', function () {
                resolve();
            });
            _this.delegate.stream.getTracks().forEach(function (track) {
                track.stop();
            });
            _this.delegate.stop();
        });
    };
    WebMMediaRecording.prototype.pause = function () {
        var _this = this;
        return new Promise(function (resolve, reject) {
            if (_this.delegate === null) {
                reject(new Error('not started'));
            }
            // this event should fire after any data is de-queued
            _this.delegate.addEventListener('pause', function () {
                resolve();
            });
            _this.delegate.pause();
        });
    };
    WebMMediaRecording.prototype.unpause = function () {
        var _this = this;
        return new Promise(function (resolve, reject) {
            if (_this.delegate === null) {
                reject(new Error('not started'));
            }
            resolve();
            _this.delegate.resume();
        });
    };
    Object.defineProperty(WebMMediaRecording.prototype, "recordingState", {
        get: function () {
            return this.delegate.state;
        },
        enumerable: true,
        configurable: true
    });
    WebMMediaRecording.prototype.addEventListener = function (type, listener) {
        var _this = this;
        Maybe_1.default.of(this.listeners.get(type))
            .defaulting(new Set())
            .map(function (listeners) { return listeners.add(listener); })
            .map(function (listeners) { return _this.listeners.set(type, listeners); });
    };
    WebMMediaRecording.prototype.dispatchEvent = function (event) {
        Maybe_1.default.of(this.listeners.get(event.type)).map(function (listeners) {
            listeners.forEach(function (listener) { return listener(event); });
        });
        return event.defaultPrevented;
    };
    WebMMediaRecording.prototype.removeEventListener = function (type, listener) {
        Maybe_1.default.of(this.listeners.get(type)).map(function (f) { return f.delete(listener); });
    };
    WebMMediaRecording.browser = new DefaultBrowserBehavior_1.default();
    WebMMediaRecording.options = {
        mimeType: 'video/webm; codecs=vp8',
    };
    return WebMMediaRecording;
}());
exports.default = WebMMediaRecording;
//# sourceMappingURL=WebMMediaRecording.js.map